import Input from '@ben/orion/input';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useMinMaxInput from '../../../../hooks/use-min-max-input';
import MinMaxConfiguration from '../_common/min-max-configuration';

interface IModularAmountInputProps {
  componentControllerName: string;
}

const ModularAmountInput = ({
  componentControllerName,
}: IModularAmountInputProps) => {
  const form = useFormContext();
  const { control } = form;

  const { isFixedNumber, setIsFixedNumber, renderMinMaxInput } = useMinMaxInput(
    {
      componentControllerName,
      form,
      validationMessage: {
        min: 'Min value should be less than max value',
        max: 'Max value should be greater than min value',
      },
    }
  );

  return (
    <Wrapper>
      <InputWrapper>
        <Controller
          name={`${componentControllerName}.default`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Default value (optional)"
              type="number"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
          rules={{
            validate: (value) => {
              if (!value || !isFixedNumber) return true;
              const min = form.watch(`${componentControllerName}.min`);
              const max = form.watch(`${componentControllerName}.max`);
              if (
                min &&
                max &&
                (Number(value) < Number(min) || Number(value) > Number(max))
              ) {
                return 'Default value should be between min and max values';
              }
              return true;
            },
          }}
        />
        <Controller
          name={`${componentControllerName}.interval`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Interval (optional)"
              type="number"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              hintText="Specifies how much values change between points. For example, an increment of 0.5 means values change by 0.5 units per step. The most common interval is 1."
            />
          )}
        />
      </InputWrapper>
      <MinMaxConfiguration
        isFixedNumber={isFixedNumber}
        setIsFixedNumber={setIsFixedNumber}
      />
      <InputWrapper>
        {renderMinMaxInput({
          isMin: true,
          hintText:
            'Defines the minimum amount the employee is allowed to enter.',
          name: 'Min value accepted',
        })}
        {renderMinMaxInput({
          isMin: false,
          hintText:
            'Defines the maximum amount the employee is allowed to enter.',
          name: 'Max value accepted',
        })}
      </InputWrapper>
    </Wrapper>
  );
};

export default ModularAmountInput;

const Wrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const InputWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;
